import React from "react";
import { Body } from "@sprout/typography";

import useTranslation from "@hooks/use_translation";
import { formatDate } from "@/utils/date_utils";
import { SecondaryButtonLink } from "@sprout/buttons";
import { identityVerificationPartnerConfig } from "@/utils/identity_verification_provider";

type StatusKey =
  | "success"
  | "fail"
  | "awaiting_user_input"
  | "processing_data"
  | "expired"
  | "request_canceled"
  | "canceled"
  | "geo_lock";

type Props = {
  status?: string;
  companyName?: string;
  verificationUrl?: string;
  completedAt?: string | null;
  failedAt?: string | null;
  updatedAt?: string | null;
  partner?: string | null;
  children?: React.ReactNode;
};

const isStatusKey = (value?: string): value is StatusKey =>
  !!value &&
  [
    "success",
    "fail",
    "awaiting_user_input",
    "processing_data",
    "expired",
    "request_canceled",
    "canceled",
    "geo_lock",
  ].includes(value as StatusKey);

const PILL_BY_STATUS: Record<StatusKey, string> = {
  success: "bg-green-e50",
  fail: "bg-poppy-e50",
  awaiting_user_input: "bg-iris-e50",
  processing_data: "bg-marigold-e50",
  expired: "bg-poppy-e50",
  request_canceled: "bg-poppy-e50",
  canceled: "bg-poppy-e50",
  geo_lock: "bg-poppy-e50",
};

export const IdentityVerificationStatusDisplay: React.FC<Props> = ({
  status,
  companyName,
  verificationUrl,
  completedAt,
  failedAt,
  updatedAt,
  partner,
  children,
}) => {
  const { t, i18n } = useTranslation("identity_verification");

  if (!isStatusKey(status)) return null;

  const label = t(`status.${status}.label`);
  const partnerConfig = identityVerificationPartnerConfig(partner);
  const message = t(`status.${status}.message`, {
    company_name: companyName,
    provider_name: partnerConfig.name,
  });
  const pillBg = PILL_BY_STATUS[status];

  const lastChangedAt = (() => {
    switch (status) {
      case "success":
        return formatDate(completedAt, i18n.language || "en");
      case "fail":
        return formatDate(failedAt, i18n.language || "en");
      default:
        return formatDate(updatedAt, i18n.language || "en");
    }
  })();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className={`px-3 py-1 rounded-lg text-[14px] leading-5 ${pillBg || "bg-muted"}`}>
          {label}
        </span>
        {lastChangedAt && <span className="text-[12px] text-foreground">{lastChangedAt}</span>}
      </div>

      <Body className="text-base">{message}</Body>

      <div className="flex gap-3 items-center flex-wrap">
        {status === "awaiting_user_input" && verificationUrl && (
          <SecondaryButtonLink size="medium" href={verificationUrl} external>
            {t("cta.continue_verification")}
          </SecondaryButtonLink>
        )}
        {children}
      </div>
    </div>
  );
};

export default IdentityVerificationStatusDisplay;
